import { ExternalLink } from "lucide-react";
import { Reveal, MaskedLine } from "../components/site/Reveal";
import { usePageMeta } from "../lib/seo";

export default function About() {
  usePageMeta(
    "About | VeraScreen Solutions — DC & Maryland Workforce Screening Experts",
    "VeraScreen Solutions is a certified DOT specimen collection site and compliance resource serving employers across Washington DC, Maryland, and the DMV region. A division of The Allicock Group."
  );

  return (
    <main data-testid="about-page">
      <section className="relative overflow-hidden bg-[#1A3A6B] py-24 md:py-28">
        <div className="grain-overlay" />
        <div className="hero-grid absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.1}>Who We Are</MaskedLine>
          </h1>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 md:grid-cols-2 lg:px-8">
          <Reveal>
            <div className="space-y-6 text-base leading-relaxed text-[#333333]/90">
              <p>
                VeraScreen Solutions is a professional drug testing and workforce screening company
                serving employers across Washington DC, Maryland, and the broader DMV region. We are
                a certified DOT specimen collection site and compliance resource for organizations
                that need accurate results, reliable service, and a partner who understands the
                regulatory environment they operate in.
              </p>
              <p>
                We provide pre-employment, random, post-accident, and federal drug testing programs
                alongside BAT-certified alcohol testing, fingerprinting services, and DC and
                Maryland-specific compliance consulting. Our mobile capability means we come to your
                worksite — minimizing disruption and maximizing responsiveness when time-sensitive
                situations arise.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="h-full bg-[#F5F5F5] p-8 md:p-10">
              <h2 className="font-display text-2xl font-extrabold tracking-tight text-[#0E7B6B]">
                Our Approach
              </h2>
              <div className="mt-5 space-y-5 text-sm leading-relaxed text-[#333333]/85">
                <p>
                  Workplace screening is not just a checkbox. Done right, it protects your
                  employees, limits your liability, and builds a culture of accountability and
                  safety. Done wrong, it exposes your organization to legal risk, compliance
                  violations, and costly errors.
                </p>
                <p>
                  VeraScreen was built on the belief that employers deserve a screening partner who
                  takes compliance as seriously as they do. Every collection we perform follows
                  federal DOT and SAMHSA guidelines. Every result is documented with complete chain
                  of custody integrity. And every client interaction is handled with the
                  professionalism and discretion this work demands.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#E7F2F0] py-20 md:py-24" data-testid="about-expertise">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-[#1A3A6B] md:text-4xl">
              Our Expertise
            </h2>
            <p className="mt-6 max-w-4xl text-base leading-relaxed text-[#333333]/85 md:text-lg">
              We bring deep knowledge of DC and Maryland employer drug testing law — including the
              nuances of DC's Cannabis Employment Protections Amendment Act and Maryland's cannabis
              employment protections — that most regional testing providers simply don't offer. For
              federal contractors and DOT-regulated employers operating in the DMV, that expertise
              is not optional. It is essential.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28" data-testid="about-tag-section">
        <div className="mx-auto max-w-3xl px-5 text-center lg:px-8">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#B8962E]">Our Parent Company</p>
            <h2 className="font-display mt-4 text-3xl font-extrabold tracking-tight text-[#1A3A6B] md:text-4xl">
              A Division of The Allicock Group
            </h2>
            <p className="mt-6 text-base leading-relaxed text-[#333333]/85">
              VeraScreen Solutions is a division of The Allicock Group, a multi-division workforce
              solutions firm based in the DC metro area. The Allicock Group serves employers across
              staffing, clinical services, workforce training, and screening — bringing integrated
              expertise to every client relationship.
            </p>
            <a
              href="https://www.theallicockgroup.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-tag-link"
              className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-[#0E7B6B] transition-colors duration-200 hover:text-[#1A3A6B]"
            >
              Learn more about The Allicock Group <ExternalLink size={15} />
            </a>
            <p className="mt-2 text-xs italic text-gray-400">[TAG website URL placeholder — confirm before launch]</p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
